/**
 * attach_evidence — structural agent tool. Wraps the manual-attach
 * REST endpoint with a preview/confirm lifecycle so the user reviews
 * the snippet before it's persisted.
 */

import type { IAgentTool, IAgentToolContext, AgentToolResult } from '../../../../core/ai/agent/IAgentTool';

type ArtifactKind = 'pdf-snippet' | 'screenshot' | 'log' | 'config-json' | 'policy-quote';
const ARTIFACT_KINDS: readonly ArtifactKind[] = [
  'pdf-snippet',
  'screenshot',
  'log',
  'config-json',
  'policy-quote',
];

function getFolderId(context: IAgentToolContext): string | null {
  const ctx = context.assistantContext as Record<string, unknown> | undefined;
  const f = ctx?.folderId;
  return typeof f === 'string' && f.length > 0 ? f : null;
}

export const attachEvidenceTool: IAgentTool = {
  name: 'attach_evidence',
  description:
    'Attach a piece of evidence (policy quote, log line, config snippet, screenshot reference) to a compliance control in the current folder. Returns a preview the user must confirm before the evidence is persisted.',
  parameters: {
    type: 'object',
    properties: {
      controlId: { type: 'string', description: 'Catalog control id, e.g. "CC6.1".' },
      framework: { type: 'string', description: 'Framework id (default soc2).' },
      artifactKind: {
        type: 'string',
        description: 'pdf-snippet | screenshot | log | config-json | policy-quote.',
      },
      snippetText: { type: 'string', description: 'The evidence text content.' },
      caption: { type: 'string', description: 'Short title shown in the audit drawer.' },
    },
    required: ['controlId', 'artifactKind', 'snippetText'],
  },
  supportedDiagramTypes: [],
  tier: 'structural',
  category: 'edit',

  async execute(params, context): Promise<AgentToolResult> {
    const folderId = getFolderId(context);
    if (!folderId) return { success: false, summary: 'No folder context available.' };
    const controlId = String(params.controlId ?? '');
    const artifactKind = String(params.artifactKind ?? '') as ArtifactKind;
    const snippetText = String(params.snippetText ?? '');
    if (!controlId || !snippetText) {
      return { success: false, summary: 'controlId and snippetText are required.' };
    }
    if (!ARTIFACT_KINDS.includes(artifactKind)) {
      return {
        success: false,
        summary: `Unknown artifactKind "${artifactKind}". Use one of: ${ARTIFACT_KINDS.join(', ')}.`,
      };
    }
    const framework = typeof params.framework === 'string' && params.framework ? params.framework : 'soc2';
    const caption = typeof params.caption === 'string' ? params.caption : null;

    const url =
      `/api/compass/folders/${encodeURIComponent(folderId)}` +
      `/compliance/${encodeURIComponent(framework)}` +
      `/controls/${encodeURIComponent(controlId)}/artifacts`;

    return {
      success: true,
      summary: `Ready to attach a ${artifactKind} exhibit to ${controlId}. Confirm to persist.`,
      uiSurface: 'chat',
      preview: {
        description:
          `Attach ${artifactKind} to ${controlId}` +
          (caption ? ` (caption: ${caption})` : '') +
          `:\n\n${snippetText.slice(0, 400)}${snippetText.length > 400 ? '…' : ''}`,
        delta: { additions: [], removals: [], moves: [], updates: [] },
        confirm: {
          kind: 'external-effect',
          run: async () => {
            const r = await fetch(url, {
              method: 'POST',
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify({
                artifactKind,
                snippetText,
                caption,
              }),
            });
            if (!r.ok) {
              throw new Error(`Attach failed (${r.status})`);
            }
          },
        },
      },
    };
  },
};
